/**
 * HelperListEmailModal Component
 *
 * Modal for sending email to visible helpers from the Registrierte Helfer list.
 * Two options: open in email client (mailto) or send via system (Helfermanagement as from).
 * Subject and body are editable. BCC contains visible helper emails.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import './HelperListEmailModal.css';

interface HelperListEmailModalProps {
  onClose: () => void;
  bccEmails: string[];
}

const HelperListEmailModal: React.FC<HelperListEmailModalProps> = ({ onClose, bccEmails }) => {
  const { token } = useAuth();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const validBccEmails = bccEmails.filter(e => e && e.trim());
  const recipientCount = validBccEmails.length;

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending && !success) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, sending, success]);

  const handleOpenInEmailClient = () => {
    if (!subject.trim()) {
      setError('Bitte gib einen Betreff ein.');
      return;
    }
    if (!body.trim()) {
      setError('Bitte gib einen Nachrichtentext ein.');
      return;
    }
    if (validBccEmails.length === 0) {
      setError('Keine gültigen E-Mail-Adressen für die sichtbaren Helfer.');
      return;
    }
    setError(null);
    const bccParam = validBccEmails.join(',');
    const mailto = `mailto:?bcc=${encodeURIComponent(bccParam)}&subject=${encodeURIComponent(subject.trim())}&body=${encodeURIComponent(body.trim())}`;
    window.location.href = mailto;
    onClose();
  };

  const handleSendViaSystem = async () => {
    if (!subject.trim()) {
      setError('Bitte gib einen Betreff ein.');
      return;
    }
    if (!body.trim()) {
      setError('Bitte gib einen Nachrichtentext ein.');
      return;
    }
    if (validBccEmails.length === 0) {
      setError('Keine gültigen E-Mail-Adressen für die sichtbaren Helfer.');
      return;
    }
    if (!token) {
      setError('Du bist nicht angemeldet. Bitte melde dich erneut an.');
      return;
    }
    setError(null);
    setSending(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/helpers/email`,
        {
          bccEmails: validBccEmails,
          subject: subject.trim(),
          body: body.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess(true);
      } else {
        setError(response.data.message || 'Fehler beim Senden der E-Mail');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string }; status?: number }; message?: string };
      const errorMessage =
        axiosErr.response?.data?.message || axiosErr.message || 'Fehler beim Senden der E-Mail';
      setError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending && !success) {
      onClose();
    }
  };

  const handleSendAnother = () => {
    setSuccess(false);
    setSubject('');
    setBody('');
    setError(null);
  };

  return (
    <div className="helper-list-email-overlay" onClick={handleClose}>
      <div className="helper-list-email-container" onClick={(e) => e.stopPropagation()}>
        <button
          className="helper-list-email-close"
          onClick={handleClose}
          disabled={sending}
          title="Schließen"
        >
          ×
        </button>

        <div className="helper-list-email-header">
          <h2 className="helper-list-email-title">E-Mail an sichtbare Helfer</h2>
        </div>

        {success ? (
          <div className="helper-list-email-content">
            <div className="helper-list-email-success">
              <div className="helper-list-email-success-icon">✓</div>
              <h3>E-Mail erfolgreich gesendet!</h3>
              <p>Die E-Mail wurde an {recipientCount} Helfer gesendet.</p>
              <div className="helper-list-email-actions">
                <button
                  type="button"
                  className="helper-list-email-button helper-list-email-button-primary"
                  onClick={handleSendAnother}
                >
                  Weitere E-Mail senden
                </button>
                <button
                  type="button"
                  className="helper-list-email-button helper-list-email-button-secondary"
                  onClick={onClose}
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="helper-list-email-content">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendViaSystem();
              }}
              className="helper-list-email-form"
            >
              <div className="helper-list-email-header-fields">
                <div className="helper-list-email-field">
                  <label htmlFor="helper-email-from">Von:</label>
                  <input
                    type="text"
                    id="helper-email-from"
                    value="Helfermanagement"
                    disabled
                    className="helper-list-email-input helper-list-email-input-disabled"
                  />
                </div>
                <div className="helper-list-email-field">
                  <label htmlFor="helper-email-bcc">BCC:</label>
                  <input
                    type="text"
                    id="helper-email-bcc"
                    value={`${recipientCount} Empfänger (sichtbare Helfer)`}
                    disabled
                    className="helper-list-email-input helper-list-email-input-disabled"
                  />
                </div>
                <div className="helper-list-email-field">
                  <label htmlFor="helper-email-subject">Betreff <span className="required">*</span></label>
                  <input
                    type="text"
                    id="helper-email-subject"
                    value={subject}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      setError(null);
                    }}
                    placeholder="Betreff eingeben..."
                    disabled={sending}
                    className="helper-list-email-input"
                  />
                </div>
              </div>

              <div className="helper-list-email-form-group">
                <label htmlFor="helper-email-body">
                  Nachricht <span className="required">*</span>
                </label>
                <textarea
                  id="helper-email-body"
                  value={body}
                  onChange={(e) => {
                    setBody(e.target.value);
                    setError(null);
                  }}
                  placeholder="Nachricht eingeben..."
                  rows={12}
                  disabled={sending}
                  className="helper-list-email-textarea"
                />
                <div className="helper-list-email-char-count">{body.length} Zeichen</div>
              </div>

              {error && <div className="helper-list-email-error">{error}</div>}

              <div className="helper-list-email-actions">
                <button
                  type="button"
                  className="helper-list-email-button helper-list-email-button-secondary"
                  onClick={handleClose}
                  disabled={sending}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="helper-list-email-button helper-list-email-button-outline"
                  onClick={handleOpenInEmailClient}
                  disabled={sending || !subject.trim() || !body.trim() || recipientCount === 0}
                  title="E-Mail in deinem Standard-E-Mail-Programm öffnen (BCC: sichtbare Helfer)"
                >
                  In E-Mail-Client öffnen
                </button>
                <button
                  type="submit"
                  className="helper-list-email-button helper-list-email-button-primary"
                  disabled={sending || !subject.trim() || !body.trim() || recipientCount === 0}
                >
                  {sending ? 'Wird gesendet...' : 'Per System senden'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelperListEmailModal;
